import type { CSSProperties } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Path } from '@/routes/paths'
import { APP_NAME } from '../../constants'
import wordmark from '../../assets/logos/wordmark.svg'
import brandIcon from '../../assets/logos/icon.svg'
import GlobalPanelLauncher from '@features/panels/components/GlobalPanelLauncher'
import { getAppModuleLabelForPath } from '../../config/appNavigation'
import { usePageTrailSlotRef } from '@features/shared/components/pageTrailSlot'
import GlobalSearchBar from '../../features/search/components/GlobalSearchBar'
import HeaderThemeToggle from '@features/settings/components/HeaderThemeToggle'
import { SHARE_ICON_SIZE } from '@features/sharing/components/PageShareButton'

const appHeaderBase =
  'grid h-[var(--header-height)] w-full shrink-0 grid-cols-[auto_minmax(var(--header-control-size),1fr)_auto] items-center bg-[color:var(--surface-header)] text-default shadow-[inset_0_-1px_0_var(--surface-header-edge)]'

// The wordmark is drawn through its own shape in --brand-wordmark, so its ink
// follows the theme (the logo's slate on light, near-white on dark).
const wordmarkMask = `url("${wordmark}")`
const wordmarkStyle = {
  maskImage: wordmarkMask,
  WebkitMaskImage: wordmarkMask,
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
}

// The rail's line between its groups, upright: the muted sidebar line, as
// long as an icon (AppSidebar's groupDivider).
function HeaderDivider({ style }: { style?: CSSProperties }) {
  return (
    <span
      className="h-sidebar-module-size w-px shrink-0 bg-[color:var(--sidebar-border-muted)]"
      style={style}
      aria-hidden="true"
    />
  )
}

// How much further a module name's first letter stands from the line before
// it than the wordmark's last letter does after it (Inter, by the mean of each
// letter's outline on the facing side): a T's stem sits well inside its
// crossbar, and A and S lean away. The name is pulled in by that much, so the
// gaps either side of the line read even; other letters stand as the e does.
const NAME_OPTICAL_PULL_EM: Partial<Record<string, number>> = { T: 0.17, S: 0.1, A: 0.08 }

// The line before the panel buttons sits midway between the Theme toggle's
// border and the share glyph, not the share button's box, which shows only on
// hover: nudged right by half the room the glyph leaves on each side of it.
const PANEL_DIVIDER_STYLE: CSSProperties = {
  transform: `translateX(calc((var(--header-control-size) - ${SHARE_ICON_SIZE}px) / 4))`,
}

export default function AppHeader() {
  const location = useLocation()
  const moduleName = getAppModuleLabelForPath(location.pathname) ?? 'Tables'
  const trailSlotRef = usePageTrailSlotRef()

  return (
    <header className={`app-header ${appHeaderBase}`}>
      <div className="app-header-identity flex h-full min-w-0 max-w-[min(60vw,52rem)] items-center">
        <Link
          to={Path.home}
          aria-label={`${APP_NAME} home`}
          className="app-header-brand ml-3 shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-info)]"
        >
          <span
            aria-hidden="true"
            className="app-header-wordmark block h-[14px] w-[70px] bg-[color:var(--brand-wordmark)]"
            style={wordmarkStyle}
          />
          <img
            src={brandIcon}
            alt=""
            aria-hidden="true"
            className="app-header-brand-icon hidden h-6 w-6"
          />
        </Link>
        <span className="app-header-module-divider ml-4 inline-flex">
          <HeaderDivider />
        </span>
        {/* The module's name starts the page's path; the page's trail
            continues it in the spot after it (see pageTrailSlot). */}
        <span
          className="app-header-module shrink-0 text-sm font-medium leading-none text-subtle"
          style={{ marginLeft: `calc(1rem - ${NAME_OPTICAL_PULL_EM[moduleName[0]] ?? 0}em)` }}
        >
          {moduleName}
        </span>
        <span ref={trailSlotRef} className="app-header-trail flex min-w-0 items-center pr-4" />
      </div>
      <div className="app-header-search flex h-full min-w-[var(--header-control-size)] items-center justify-center px-[min(4rem,max(0px,calc((100%_-_var(--header-control-size))/2)))]">
        <GlobalSearchBar />
      </div>
      {/* Inset like the cards below, so its last control's right edge lines
          up with theirs. Theme, then a line, then the panels. */}
      <div className="app-header-actions flex h-full items-center gap-3 pr-[var(--page-card-gap)]">
        <HeaderThemeToggle />
        <HeaderDivider style={PANEL_DIVIDER_STYLE} />
        <GlobalPanelLauncher />
      </div>
    </header>
  )
}
